import React, { useContext, useMemo, FC } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { linkColumn } from '../../../components/BaseTable/BaseTable';
import FAIcon from '../../../components/FAIcon/FAIcon';
import FilterableTable from '../../../components/FilterableTable/FilterableTable';
import Loading from '../../../components/Loading/Loading';
import Page from '../../../components/Page/Page';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { TabState } from '../../../components/StatefulTabs/StatefulTabs';
import StatusText from '../../../components/StatusText/StatusText';
import DateCell from '../../../components/cells/DateCell/DateCell';
import { digitalCodesConstants } from '../../../constants/digitalCodesConstants';
import { getCodeInBoxRequests } from '../../../services/digitalCodesService';
import { displayString } from '../../../utils/stringUtils';
import { displayStringForCIBPriceType, getProductGameCode } from '../../../utils/digitalCodesUtils';

const {
	PENDING_REVIEW,
	REVIEWED,
	REQUESTED,
} = digitalCodesConstants.priceRequestStatuses;

const price_types: Record<string, string> = {
	WHOLESALE: 'Wholesale',
	DL_SIZE: 'ROM Size',
};

interface DigitalCodeRequestsProps {
	eventKey: string;
}
const DigitalCodeRequests: FC<DigitalCodeRequestsProps> = ({ eventKey }) => {
	const show = useContext(TabState)?.tab === eventKey;
	const requestsQuery = useQuery('getCodeInBoxRequests', () => getCodeInBoxRequests(), { enabled: show });
	const requests = requestsQuery.data?.data;

	const isLoading = requestsQuery.isLoading;

	const columns = useMemo(
		() => [
			{
				Header: 'Product',
				id: 'product_column',
				accessor: (row: Record<string, any>) => {
					const game_code = getProductGameCode(row);
					return `[${game_code.substring(game_code.length - 5)}] ${row.product_game_name}`;
				},
				minWidth: 200,
			},
			{
				Header: 'Component',
				id: 'component_column',
				accessor: ({ ns_uid, game_name }: { ns_uid: string, game_name: string }) =>
					`[${ns_uid}] ${game_name}`,
				minWidth: 200,
			},
			{
				Header: 'Pricing Method',
				accessor: 'price_type_code',
				Cell: ({ value }: { value: string; }) => displayStringForCIBPriceType(value),
			},
			{ Header: 'Received', accessor: 'submitted_datetime', Cell: DateCell },
			{
				Header: 'Status',
				accessor: 'request_status',
				Cell: ({ value }: { value: string; }) => <StatusText>{value}</StatusText>,
			},
			{
				...linkColumn,
				Cell: ({ original }: { original: ArrayElement<NonNullable<typeof requests>>; }) => {
					const newPath = `/products/components/${original.ns_uid}?tab=cib-price-requests`;
					return (
						<Link to={newPath}>
							<FAIcon name="chevron-right" />
						</Link>
					);
				},
			},
		],
		[],
	);

	const filterProperties = useMemo(() => new Map(
		Object.entries({
			'Pricing Method': {
				filter: 'price_type_code',
				selectableFilters: new Set(Object.values(price_types)),
				customEvaluation: (value: string, filters: Set<string>) => filters.has(price_types[value]),
			},
			'Status': {
				filter: 'request_status',
				selectableFilters: new Set([
					displayString(PENDING_REVIEW),
					displayString(REVIEWED),
					displayString(REQUESTED),
				]),
			}
		})
	), []);

	return (
		<Page.ContentContainer>
			<SectionTitle>Code-in-Box Price Requests</SectionTitle>
			{isLoading ? (
				<Loading />
			) : (
				<FilterableTable
					data={requests}
					dataFormat={columns}
					filterProperties={filterProperties}
					searchableFields={['game_name', 'ns_uid']}
					searchableFieldPlaceHolder={'Search by Component Name, NSUID'}
					noBorder
					retainPageState
					stateOnTab={eventKey}
				/>
			)}
		</Page.ContentContainer>
	);
};
export default DigitalCodeRequests;
